/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { IDialogProps } from '@fluentui/react/lib/Dialog';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { isAuthoringResource } from 'src/core/utils/csAccountUtils';
import { AzureCsAccountCreateForm, AzureCsAccountCreateFormDto } from 'src/modules/azure/components/AzureCsAccountCreateForm';
import { AzureCsAccountSelectForm } from 'src/modules/azure/components/AzureCsAccountSelectForm';
import { AzureMigrationModalCsAccountDto, getBackwardCompatibleRegion } from 'src/verticals/azureMigrationModal/MultiRegionMigrationModal';
import { DeleteUserModal } from 'src/verticals/azureMigrationModal/newComponents/DeleteUserModal';
import { MigrationModalFooter } from 'src/verticals/azureMigrationModal/newComponents/MigrationModalFooter';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';

export type AuthoringResourceContainerScreenProps = IDialogProps & {
    isLoading: boolean;
    userMigrationInfoStore: UserMigrationInfoStore;
    onPrevious: () => void;
    onContinue: () => void;
    onMigrate: (dto: AzureMigrationModalCsAccountDto) => void;
};

const messages = defineMessages({
    title: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.title',
        defaultMessage: 'Migrate in "{ region }"'
    },
    migrateButton: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.migrateButton',
        defaultMessage: 'Migrate'
    },
    migrationProgress: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.migrationProgress',
        defaultMessage: 'Migration in progress ...'
    },
    previousButton: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.previousButton',
        defaultMessage: 'Back'
    },
    instructions: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.instructions',
        defaultMessage: 'Note: By default, your apps will be shared will other members in this subscription.'
    },
    useExistingAccount: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.useExistingAccount',
        defaultMessage: 'Use existing authoring resource'
    },
    createNewAccount: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.createNewAccount',
        defaultMessage: 'Create new authoring resource'
    },
    deleteUserLink: {
        id: 'multiRegionMigrationModal.authoringResourceContainerScreen.deleteUserLink',
        defaultMessage: 'Delete your user in this region'
    }
});

type AccountOptions = 'existing' | 'new';

/**
 * Represents a modal to select or create a Luis authoring resource.
 */
export const AuthoringResourceContainerScreen = observer((props: AuthoringResourceContainerScreenProps) => {
    const { userMigrationInfoStore, isLoading, onMigrate, onPrevious, onContinue } = props;
    const { localize } = useLocalization();
    const [accountMode, setAccountMode] = React.useState<AccountOptions>('existing');
    const [csAccountToCreate, setCsAccountToCreate] = React.useState<AzureCsAccountCreateFormDto>();
    const [csAccountToSelect, setCsAccountToSelect] = React.useState<AzureCsAccountStore>();
    const [isDeleteAccountModalHidden, setIsDeleteAccountModalHidden] = React.useState(true);

    const regionToMigrate = userMigrationInfoStore?.region;

    const options: IChoiceGroupOption[] = [
        {
            key: 'existing',
            text: localize(messages.useExistingAccount),
            styles: { choiceFieldWrapper: { width: '100%' } },
            onRenderField: (optionProps, render) => (
                    <Stack tokens={{ childrenGap: 15, padding: '0 0 20px 0' }}>
                        {render && render(optionProps)}
                        {accountMode === 'existing' && !isLoading && (
                            <AzureCsAccountSelectForm
                                mode="LUIS.Authoring"
                                onCsAccountChange={setCsAccountToSelect}
                                filterPredicate={a => isAuthoringResource(a) && regionToMigrate === a.region}
                            />
                        )}
                    </Stack>
                )
        },
        {
            key: 'new',
            text: localize(messages.createNewAccount),
            styles: { choiceFieldWrapper: { width: '100%' } },
            onRenderField: (optionProps, render) => (
                    <Stack tokens={{ childrenGap: 15 }}>
                        {render && render(optionProps)}
                        {accountMode === 'new' && !isLoading && (
                            <AzureCsAccountCreateForm
                                showRegion={false}
                                mode="LUIS.Authoring"
                                showPricingTier={false}
                                showManagedIdentityToggle={false}
                                onAzureCsAccountDtoChange={setCsAccountToCreate}
                            />
                        )}
                    </Stack>
                )
        }
    ];

    const isNextEnabled = () => {
        if (accountMode === 'existing') {
            return Boolean(csAccountToSelect);
        }
            return csAccountToCreate?.isValid;
    };

    const onSelectAndMigrate = () => onMigrate({
            name: csAccountToSelect.name,
            subscriptionId: csAccountToSelect.subscriptionId,
            resourceGroupName: csAccountToSelect.resourceGroupName
        });

    const onCreateAndMigrate = () => onMigrate({
            name: csAccountToCreate.name,
            subscriptionId: csAccountToCreate.subscriptionId,
            resourceGroupName: csAccountToCreate.resourceGroupName
        });

    const handleDeleteUserAccount = () => {
        setIsDeleteAccountModalHidden(true);
        onContinue();
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 40 }}>
            <Stack verticalFill tokens={{ childrenGap: 15 }}>
                <LuisText variant="xLarge" fontWeight={600}>
                    {localize(messages.title, { region: regionToMigrate ?? getBackwardCompatibleRegion() })}
                </LuisText>

                <LuisText>{localize(messages.instructions)}</LuisText>

                <ChoiceGroup
                    options={options}
                    disabled={isLoading}
                    selectedKey={accountMode}
                    onChange={(_, option) => setAccountMode(option.key as AccountOptions)}
                />

                <Link onClick={() => setIsDeleteAccountModalHidden(false)}>{localize(messages.deleteUserLink)}</Link>
            </Stack>

            {!isDeleteAccountModalHidden && (
                <DeleteUserModal
                    userMigrationInfoStore={userMigrationInfoStore}
                    onDismiss={() => setIsDeleteAccountModalHidden(true)}
                    onDelete={handleDeleteUserAccount}
                />
            )}

            <MigrationModalFooter
                isLoading={isLoading}
                onPrevious={onPrevious}
                isRegionMigrationStep
                nextDisabled={!isNextEnabled()}
                onContinue={accountMode === 'existing' ? onSelectAndMigrate : onCreateAndMigrate}
            />
        </Stack>
    );
});
